import { CaptureConsole, ExtraErrorData, HttpClient } from '@sentry/integrations';
import { HttpStatusCodeRange } from '@sentry/integrations/types/httpclient';
import * as SentryClient from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import { EnvironmentService } from '@/services/Environment';
import { parseJwtPayload } from '@/utils/parse-jwt-payload';

class SentryService {
  private get isEnabled() {
    // Is dev added in testing purpose
    return EnvironmentService.isCERT || EnvironmentService.isPROD || EnvironmentService.isDEV;
  }

  public init() {
    if (!this.isEnabled) {
      return;
    }

    const failedRequestStatusCodes: HttpStatusCodeRange[] = [400, [402, 599]];

    SentryClient.init({
      dsn: import.meta.env.VITE_SENTRY_DSN_URL,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      ignoreErrors: ['top.GLOBALS'],
      allowUrls: [/(\w)+.gaf.energy/], // Add /https:\/\/localhost:44334/ if you need to test integrations locally
      integrations: [
        new BrowserTracing(),
        new SentryClient.Replay(),
        new ExtraErrorData(),
        new CaptureConsole({
          levels: ['error'],
        }),
        new HttpClient({
          failedRequestStatusCodes,
          failedRequestTargets: [/(\w)+.gaf.energy/],
        }),
      ],
      // This sets the sample rate to be 5%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: EnvironmentService.isDEV ? 0 : 0.05,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: EnvironmentService.isDEV ? 0 : 1.0,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: EnvironmentService.isPROD ? 1.0 : 0.25,
      environment: EnvironmentService.environment,
    });
  }

  public setUser() {
    if (!this.isEnabled) {
      return;
    }

    const { companyId, companyName, email, role, userId, assistedByAdmin } = parseJwtPayload();

    SentryClient.setUser({ companyId, companyName, email, role, userId, assistedByAdmin });
  }

  public clearUser() {
    if (!this.isEnabled) {
      return;
    }

    SentryClient.setUser(null);
  }

  public captureError(error: Error) {
    if (!this.isEnabled) {
      return;
    }

    SentryClient.captureException(error);
  }
}

export const Sentry = new SentryService();
